'use client';

import React, { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { appSectionPath } from '@/app/routes';
import { APP_SECTION } from '@/features/app/constants/sections';
import type { PreselectedClientForService } from '@/features/app/clients/presentation/components/ClientsSection';
import { DashboardSection } from '@/features/app/dashboard/presentation/components/DashboardSection';
import { ClientsSection } from '@/features/app/clients/presentation/components/ClientsSection';
import { RegisterServiceSection } from '@/features/app/services/presentation/components/RegisterServiceSection';
import { ListaServicosSection } from '@/features/app/services/presentation/components/ListaServicosSection';
import { ReminderSettingsSection } from '@/features/app/presentation/components/ReminderSettingsSection';
import { LembretesEnviadosSection } from '@/features/app/presentation/components/LembretesEnviadosSection';
import { ManutencoesAgendadasSection } from '@/features/app/presentation/components/ManutencoesAgendadasSection';
import { FAQSuporteSection } from '@/features/app/presentation/components/FAQSuporteSection';
import { MinhaContaSection } from '@/features/app/presentation/components/MinhaContaSection';
import { PlanosSection } from '@/features/app/presentation/components/PlanosSection';
import type { Service } from '@/core/domain';

export type AppRegistrarLocationState = {
  preselectedClient?: PreselectedClientForService | null;
  preselectedServiceForEdit?: Service | null;
};

function clearRegistrarState(navigate: ReturnType<typeof useNavigate>, pathname: string) {
  navigate(pathname, { replace: true, state: {} });
}

export function AppDashboardRoute(): React.ReactElement {
  const navigate = useNavigate();
  const goRegistrar = useCallback(() => {
    navigate(appSectionPath(APP_SECTION.REGISTRAR_SERVICO));
  }, [navigate]);
  return <DashboardSection onRegisterServiceClick={goRegistrar} />;
}

export function AppClientsRoute(): React.ReactElement {
  const navigate = useNavigate();
  const fromClient = useCallback(
    (client?: PreselectedClientForService) => {
      navigate(appSectionPath(APP_SECTION.REGISTRAR_SERVICO), {
        state: { preselectedClient: client ?? null, preselectedServiceForEdit: null } satisfies AppRegistrarLocationState,
      });
    },
    [navigate]
  );
  return <ClientsSection onRegisterServiceClick={fromClient} />;
}

export function AppRegisterServiceRoute(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as AppRegistrarLocationState;
  const preselectedClient = state.preselectedClient ?? null;
  const preselectedServiceForEdit = state.preselectedServiceForEdit ?? null;

  const onPreselectedApplied = useCallback(() => {
    clearRegistrarState(navigate, location.pathname);
  }, [navigate, location.pathname]);

  const onPreselectedServiceApplied = useCallback(() => {
    clearRegistrarState(navigate, location.pathname);
  }, [navigate, location.pathname]);

  return (
    <RegisterServiceSection
      preselectedClient={preselectedClient}
      onPreselectedApplied={onPreselectedApplied}
      preselectedServiceForEdit={preselectedServiceForEdit}
      onPreselectedServiceApplied={onPreselectedServiceApplied}
    />
  );
}

export function AppListaServicosRoute(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <ListaServicosSection
      onRegisterClick={() => {
        navigate(appSectionPath(APP_SECTION.REGISTRAR_SERVICO), {
          state: { preselectedClient: null, preselectedServiceForEdit: null } satisfies AppRegistrarLocationState,
        });
      }}
      onEditService={(service) => {
        navigate(appSectionPath(APP_SECTION.REGISTRAR_SERVICO), {
          state: { preselectedClient: null, preselectedServiceForEdit: service } satisfies AppRegistrarLocationState,
        });
      }}
    />
  );
}

export function AppLembretesEnviadosRoute(): React.ReactElement {
  return <LembretesEnviadosSection />;
}

export function AppManutencoesAgendadasRoute(): React.ReactElement {
  return <ManutencoesAgendadasSection />;
}

export function AppReminderSettingsRoute(): React.ReactElement {
  return <ReminderSettingsSection />;
}

export function AppFaqSuporteRoute(): React.ReactElement {
  return <FAQSuporteSection />;
}

export function AppMinhaContaRoute(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <MinhaContaSection
      onAlterarPlanoClick={() => navigate(appSectionPath(APP_SECTION.PLANOS))}
    />
  );
}

export function AppPlanosRoute(): React.ReactElement {
  return <PlanosSection />;
}

/** Redireciona `/app` para a secção inicial sem remontar o layout. */
export function AppIndexRedirect(): React.ReactElement {
  return <Navigate to={APP_SECTION.DASHBOARD} replace />;
}
